import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ApiUrl } from "../../../ApiUrl";

function StdViewResult() {
  const navigate = useNavigate();
  const [resultRows, setResultRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const resolveDocumentUrl = (rawUrl) => {
    if (!rawUrl) return "";

    try {
      return new URL(rawUrl).toString();
    } catch (_err) {
      // Resolve relative media paths against API base URL.
      return new URL(rawUrl, ApiUrl.apiurl).toString();
    }
  };

  const getDownloadFileName = (row, documentUrl) => {
    if (row?.uploaded_document_name) return row.uploaded_document_name;

    try {
      const pathname = new URL(documentUrl).pathname;
      const nameFromUrl = pathname.split("/").pop();
      return nameFromUrl || `Exam_Result_${row?.sr_no || "document"}`;
    } catch (_err) {
      return `Exam_Result_${row?.sr_no || "document"}`;
    }
  };

  const handleViewDocument = (row) => {
    const documentUrl = resolveDocumentUrl(row?.uploaded_document_url);
    if (!documentUrl) return;
    window.open(documentUrl, "_blank", "noopener,noreferrer");
  };

  const handleDownloadDocument = async (row) => {
    const documentUrl = resolveDocumentUrl(row?.uploaded_document_url);
    if (!documentUrl) return;

    const fileName = getDownloadFileName(row, documentUrl);

    try {
      const response = await fetch(documentUrl);
      if (!response.ok) {
        throw new Error("Unable to download document");
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = blobUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (_err) {
      const anchor = document.createElement("a");
      anchor.href = documentUrl;
      anchor.target = "_blank";
      anchor.rel = "noopener noreferrer";
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    }
  };

  const getTermLabel = (row) => {
    const rawTerm = String(row?.term_name || row?.term || row?.term_raw || row?.semester || "")
      .trim()
      .toLowerCase();

    if (!rawTerm) return "-";
    if (rawTerm === "1" || rawTerm === "01") return "Half Yearly";
    if (rawTerm === "2" || rawTerm === "02") return "Annual";
    if (rawTerm.includes("half")) return "Half Yearly";
    if (rawTerm.includes("full") || rawTerm.includes("final") || rawTerm.includes("annual")) return "Annual";

    return row?.term_name || row?.term || row?.term_raw || row?.semester || "-";
  };

  useEffect(() => {
    const fetchStudentResults = async () => {
      const studentId = sessionStorage.getItem("userId");
      const academicYearId = localStorage.getItem("academicSessionId") || "";
      const orgId = localStorage.getItem("orgId") || "";
      const branchId = localStorage.getItem("branchId") || "";

      if (!studentId) {
        setError("Student session details are missing. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const queryParams = new URLSearchParams();
        if (academicYearId) queryParams.append("academic_year_id", academicYearId);
        if (orgId) queryParams.append("org_id", orgId);
        if (branchId) queryParams.append("branch_id", branchId);

        const response = await fetch(
          `${ApiUrl.apiurl}reportcard/studentExamResult/${studentId}/?${queryParams.toString()}`
        );

        const contentType = response.headers.get("content-type") || "";
        const payload = contentType.includes("application/json")
          ? await response.json()
          : null;

        if (!response.ok || !payload || payload.message !== "Success") {
          throw new Error(payload?.message || "Unable to load exam results.");
        }

        setResultRows(Array.isArray(payload.data) ? payload.data : []);
      } catch (fetchError) {
        console.error("Error fetching exam results:", fetchError);
        setResultRows([]);
        setError(fetchError.message || "Unable to load exam results.");
      } finally {
        setLoading(false);
      }
    };

    fetchStudentResults();
  }, []);

  return (
    <div className="container-fluid">
      <div className="row">
        <div className="col-12">
          <div
            className="card p-0"
            style={{
              backgroundColor: "#dbe4f3",
              borderRadius: "10px",
              border: "1px solid #c8d2e2",
            }}
          >
            <div className="card-body p-3 p-md-4">
              <div className="row mb-3 mt-1 mx-0">
                <div className="col-12 d-flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="btn btn-danger"
                    style={{ width: "95px" }}
                    onClick={() => navigate("/student/dashboards")}
                  >
                    Close
                  </button>
                </div>
              </div>

              <p
                style={{
                  marginBottom: "16px",
                  textAlign: "center",
                  fontSize: "32px",
                  fontWeight: "700",
                  color: "#1f2937",
                }}
              >
                EXAM RESULT
              </p>

              <h4 style={{ fontSize: "34px", marginBottom: "14px", color: "#1f2937" }}>
                Uploaded Report Cards
              </h4>

              {loading ? (
                <div className="text-center py-4">Loading exam results...</div>
              ) : error ? (
                <div className="alert alert-danger mb-0">{error}</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-bordered mb-0" style={{ backgroundColor: "#fff" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#1664d9", color: "#fff" }}>
                        <th className="text-center">Sr.No</th>
                        <th className="text-center">Term</th>
                        <th className="text-center">View Document</th>
                        <th className="text-center">Download Document</th>
                      </tr>
                    </thead>
                    <tbody>
                      {resultRows.length > 0 ? (
                        resultRows.map((row, index) => {
                          const documentUrl = resolveDocumentUrl(row?.uploaded_document_url);
                          const hasDocument = Boolean(documentUrl);

                          return (
                            <tr key={`${row.result_id || "student"}-${row.sr_no || index + 1}`}>
                              <td className="text-center">{row.sr_no || index + 1}</td>
                              <td className="text-center">{getTermLabel(row)}</td>
                              <td className="text-center">
                                {hasDocument ? (
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleViewDocument(row)}
                                  >
                                    View
                                  </button>
                                ) : (
                                  <span className="text-muted">Not Uploaded</span>
                                )}
                              </td>
                              <td className="text-center">
                                {hasDocument ? (
                                  <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleDownloadDocument(row)}
                                  >
                                    Download
                                  </button>
                                ) : (
                                  <span className="text-muted">Not Uploaded</span>
                                )}
                              </td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="text-center py-3">
                            No exam results available for this student
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default StdViewResult;
